import React from 'react';
import {ArrowRight, Store, User, Clock, Package, FileText, MapPin, ExternalLink} from 'lucide-react';
import type {Visit} from '../../visits/models/visit.model';

interface VisitDetailPanelProps {
  visit: Visit | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}

function formatDateTime (iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('ar-SA');
  const time = `${d.getHours().toString().padStart(2, '0')}:${d
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;
  return `${date} ${time}`;
}

export const VisitDetailPanel: React.FC<VisitDetailPanelProps> = ({
  visit,
  loading,
  error,
  onClose
}) => {
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-100 p-3">
        <div className="font-bold text-base">
          {visit ? `تفاصيل الزيارة #${visit.id}` : 'تفاصيل الزيارة'}
        </div>
        <button
          onClick={onClose}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
          aria-label="رجوع للملخص"
        >
          <ArrowRight className="h-4 w-4" />
          رجوع
        </button>
      </div>

      <div className="p-4">
        {loading && (
          <div className="py-8 text-center text-gray-500">جارٍ تحميل التفاصيل...</div>
        )}

        {!loading && error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && visit && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span
                className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${visit.statusColor}`}
              >
                {visit.statusText}
              </span>
              <span className="text-xs text-gray-500">{formatDateTime(visit.createdAt)}</span>
            </div>

            <Section
              icon={<Store className="h-4 w-4 text-blue-600" />}
              title="المتجر"
            >
              <div className="font-semibold text-gray-900">{visit.store.storeName}</div>
              <div className="text-sm text-gray-600">
                {[visit.store.city, visit.store.region].filter(Boolean).join(' — ') || '—'}
              </div>
              {visit.store.managerName && (
                <div className="text-sm text-gray-600">
                  المدير: <span className="font-medium">{visit.store.managerName}</span>
                </div>
              )}
              {visit.store.phoneNumber && (
                <div className="text-sm text-gray-600">{visit.store.phoneNumber}</div>
              )}
            </Section>

            <Section
              icon={<User className="h-4 w-4 text-green-600" />}
              title="المسوّق"
            >
              <div className="font-semibold text-gray-900">{visit.visitor.fullName}</div>
              <div className="text-sm text-gray-600">@{visit.visitor.username}</div>
            </Section>

            <Section
              icon={<Clock className="h-4 w-4 text-amber-600" />}
              title="وقت الزيارة"
            >
              <div className="text-sm text-gray-700">
                {formatDateTime(visit.startedAt ?? visit.visitDate)}
              </div>
            </Section>

            {visit.startLocation && (
              <a
                href={`https://www.google.com/maps?q=${visit.startLocation.lat},${visit.startLocation.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 hover:bg-emerald-100"
              >
                <span className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  موقع بدء الزيارة
                </span>
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}

            {visit.visitItems && visit.visitItems.length > 0 && (
              <Section
                icon={<Package className="h-4 w-4 text-purple-600" />}
                title={`المنتجات (${visit.visitItems.length})`}
              >
                <ul className="space-y-1 text-sm text-gray-700">
                  {visit.visitItems.slice(0, 6).map((item) => (
                    <li
                      key={item.id}
                      className="flex items-center justify-between rounded border border-gray-100 bg-gray-50 px-2 py-1.5"
                    >
                      <span className="truncate">
                        {item.productName || `منتج #${item.productId}`}
                      </span>
                      <span className="flex items-center gap-2 text-xs">
                        {item.availability && (
                          <span
                            className={`rounded px-1.5 py-0.5 ${
                              item.availability === 'AVAILABLE'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {item.availability === 'AVAILABLE' ? 'متوفر' : 'غير متوفر'}
                          </span>
                        )}
                        {item.availability === 'AVAILABLE' &&
                          (item.quantity ?? item.newQuantity ?? null) !== null && (
                            <span className="text-gray-600">
                              {item.quantity ?? item.newQuantity}
                            </span>
                          )}
                      </span>
                    </li>
                  ))}
                  {visit.visitItems.length > 6 && (
                    <li className="text-xs text-gray-500">
                      و{visit.visitItems.length - 6} منتج آخر…
                    </li>
                  )}
                </ul>
              </Section>
            )}

            {visit.visitImages && visit.visitImages.length > 0 && (
              <Section
                icon={<FileText className="h-4 w-4 text-orange-600" />}
                title={`الصور (${visit.visitImages.length})`}
              >
                <div className="grid grid-cols-3 gap-2">
                  {visit.visitImages.slice(0, 6).map((img) => (
                    <a
                      key={img.id}
                      href={img.imageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aspect-square overflow-hidden rounded border border-gray-200 bg-gray-100"
                    >
                      <img
                        src={img.imageUrl}
                        alt={img.description ?? 'visit'}
                        className="h-full w-full object-cover transition-transform hover:scale-105"
                      />
                    </a>
                  ))}
                </div>
              </Section>
            )}

            {visit.notes && (
              <Section
                icon={<FileText className="h-4 w-4 text-gray-600" />}
                title="ملاحظات"
              >
                <p className="text-sm leading-relaxed text-gray-700">{visit.notes}</p>
              </Section>
            )}

            {visit.status === 'rejected' && visit.rejectReason && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3">
                <div className="text-xs font-medium text-red-700">سبب الرفض</div>
                <div className="text-sm text-red-800">{visit.rejectReason}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({icon, title, children}) => (
  <div>
    <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
      {icon}
      <span>{title}</span>
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);
